import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { createStore, useStore } from 'zustand';

type HeySynclyPageState = {
  currentChatAlias: Alias | null;
  onChatSelect: (to: Alias) => void;
  onNewChat: () => void;
};

type HeySynclyPageStore = ReturnType<typeof createHeySynclyPageStore>;

const HeySynclyPageContext = createContext<HeySynclyPageStore | null>(null);

const createHeySynclyPageStore = (initialState: HeySynclyPageState) =>
  createStore<HeySynclyPageState>(() => initialState);

type HeySynclyPageProviderProps = HeySynclyPageState & {
  children: ReactNode;
};

export function HeySynclyPageProvider({
  children,
  currentChatAlias,
  onChatSelect,
  onNewChat,
}: HeySynclyPageProviderProps) {
  const [store] = useState(() =>
    createHeySynclyPageStore({
      currentChatAlias,
      onChatSelect,
      onNewChat,
    }),
  );

  useEffect(() => {
    store.setState({
      currentChatAlias,
      onChatSelect,
      onNewChat,
    });
  }, [currentChatAlias, onChatSelect, onNewChat, store]);

  return (
    <HeySynclyPageContext.Provider value={store}>
      {children}
    </HeySynclyPageContext.Provider>
  );
}

export function useHeySynclyPageStore<T>(
  selector: (state: HeySynclyPageState) => T,
) {
  const store = useContext(HeySynclyPageContext);

  if (!store) {
    throw new Error(
      'useHeySynclyPageStore must be used within a HeySynclyPageProvider',
    );
  }

  return useStore(store, selector);
}
